// filterEngine.js
// -----------------------------------------------------------------------------
// Generic URL-driven filtering engine with jQuery, supporting:
//
//  • Multiple independent "groups" (each group = one list/section on the page)
//  • Filters (multi-select, OR-per-filter, AND-across-filters)
//  • Sorting
//  • Pagination (page X of Y, with page size)
//  • URL as the single source of truth (shareable & bookmarkable)
//  • Canonicalized URLs: normalized case/order, no defaults in query string
//  • Built-in wiring for checkboxes + <select> (optional)
//  • Programmatic control via setGroupFilters() and setGroupPage()
//
// Requires jQuery on window.$ / window.jQuery.
// Use as an ES module:
//
//     <script type="module">
//         import { createFilterEngine } from '/js/filterEngine.js';
//         ...
//     </script>
// -----------------------------------------------------------------------------

import { log } from './utils';

export function createFilterEngine(config) {
    if (!window.jQuery && !window.$) {
        throw new Error('filterEngine requires jQuery on window.$');
    }

    const $ = window.jQuery || window.$;

    const groups = config.groups || [];
    const historyMode = config.historyMode === 'push' ? 'push' : 'replace'; // 'replace' | 'push'

    const dataByKey = {};              // dataKey -> items[]
    const lastFiltersByGroupId = {};   // group.id -> last filter object (for change detection)
    let isLoading = true;              // true until finishLoading() is called

    // -------------------------------------------------------------------------
    // INTERNAL UTILS
    // -------------------------------------------------------------------------

    /**
     * Normalize a list of raw values against allowedValues:
     *  - Keep only allowed values (case-insensitive match)
     *  - Return them in the exact order & case of allowedValues
     *  - Deduplicate
     */
    function normalizeFilterList(rawValues, allowedValues) {
        const lookup = {};
        allowedValues.forEach(val => {
            lookup[val.toLowerCase()] = val;
        });

        const normalizedSet = new Set();
        rawValues.forEach(raw => {
            const canonical = lookup[String(raw).toLowerCase()];
            if (canonical) normalizedSet.add(canonical);
        });

        return allowedValues.filter(val => normalizedSet.has(val));
    }

    /**
     * Normalize a sortKey (like "rating-desc") against allowedKeys.
     * Returns { key, field, dir }:
     *  - key: canonical sort key from allowedKeys
     *  - field: data field to sort by
     *  - dir: 'asc' | 'desc'
     */
    function normalizeSortKey(rawKey, sortConfig) {
        const allowed = sortConfig.allowedKeys;
        const defaultKey = sortConfig.defaultKey;

        const input = (rawKey || defaultKey).toString().toLowerCase();
        let canonicalKey = defaultKey;

        allowed.some(opt => {
            if (opt.toLowerCase() === input) {
                canonicalKey = opt;
                return true;
            }
            return false;
        });

        const spec = sortConfig.keyToSpec(canonicalKey);
        return {
            key: canonicalKey,
            field: spec.field,
            dir: spec.dir,
        };
    }

    /**
     * Shallow equality for small filter objects.
     */
    function filtersEqual(a, b) {
        if (!a || !b) return false;
        return JSON.stringify(a) === JSON.stringify(b);
    }

    function getSearchParams() {
        return new URLSearchParams(window.location.search);
    }

    function parseCsvParam(params, key) {
        return (params.get(key) || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
    }

    /**
     * Commit URL without reloading.
     * Uses history.pushState or history.replaceState depending on config:
     *  • 'replace' (default): back button skips filter changes
     *  • 'push': back button steps through filter changes
     */
    function commitUrl(params) {
        const query = params.toString();

        const newUrl =
            window.location.pathname +
            (query ? '?' + query : '') +
            window.location.hash;
    
        if (historyMode === 'push') {
            history.pushState(null, '', newUrl);
        } else {
            history.replaceState(null, '', newUrl);
        }
    }

    // -------------------------------------------------------------------------
    // READING FILTERS + SORT FROM URL
    // -------------------------------------------------------------------------

    /**
     * For a given group, read its filters + sort from the GIVEN
     * URLSearchParams and return a normalized filter object.
     *
     * Returns:
     * {
     *   filterValues: { [filterName]: string[] },
     *   sortKey: 'title-asc',
     *   sortField: 'title',
     *   sortDir: 'asc'
     * }
     */
    function getGroupFiltersFromParams(group, params) {
        const filterValues = {};

        group.filters.forEach(filterDef => {
            const urlKey = group.id + '_' + filterDef.param;
            const rawValues = parseCsvParam(params, urlKey);
            filterValues[filterDef.name] = normalizeFilterList(
                rawValues,
                filterDef.allowedValues,
            );
        });

        const sortParamKey = group.id + '_' + group.sort.param;
        const rawSortKey = params.get(sortParamKey);
        const sortInfo = normalizeSortKey(rawSortKey, group.sort);

        return {
            filterValues,
            sortKey: sortInfo.key,
            sortField: sortInfo.field,
            sortDir: sortInfo.dir,
        };
    }

    /**
     * Ensure the URL params for this group match the CANONICAL
     * normalized filters + sort (correct case + order).
     *
     * IMPORTANT: we DO NOT write defaults into the URL:
     *  • If sort is the default sort, we remove the sort param.
     *  • If a filter has no values, we remove that filter param.
     *
     * Mutates `params` in place. Returns true if anything changed.
     */
    function canonicalizeGroupUrl(params, group, groupFilters) {
        let changed = false;

        // --- Filters ---
        group.filters.forEach(filterDef => {
            const urlKey = group.id + '_' + filterDef.param;
            const canonicalValues = groupFilters.filterValues[filterDef.name] || [];
            const canonicalStr = canonicalValues.join(',');

            const currentRaw = params.get(urlKey);

            if (!canonicalStr) {
                // No values => remove param
                if (currentRaw !== null) {
                    params.delete(urlKey);
                    changed = true;
                }
            } else if (currentRaw !== canonicalStr) {
                params.set(urlKey, canonicalStr);
                changed = true;
            }
        });

        // --- Sort ---
        const sortParamKey = group.id + '_' + group.sort.param;
        const canonicalSortKey = groupFilters.sortKey;
        const currentSortKey = params.get(sortParamKey);
        const defaultSortKey = group.sort.defaultKey;

        if (canonicalSortKey === defaultSortKey) {
            // Default sort => remove param for clean URL
            if (currentSortKey !== null) {
                params.delete(sortParamKey);
                changed = true;
            }
        } else {
            // Non-default => ensure param matches canonical key
            if (currentSortKey !== canonicalSortKey) {
                params.set(sortParamKey, canonicalSortKey);
                changed = true;
            }
        }

        return changed;
    }

    // -------------------------------------------------------------------------
    // DOM SYNC (URL -> INPUTS)
    // -------------------------------------------------------------------------

    /**
     * Sync a group's DOM inputs (checkboxes/selects) to match a
     * normalized groupFilters object (what we read from the URL).
     *
     * This keeps the UI in sync on:
     *  • first load (with URL params)
     *  • back/forward navigation
     *  • programmatic URL changes (setGroupFilters/setGroupPage)
     */
    function syncGroupDomFromFilters(group, groupFilters) {
        const filterValues = groupFilters.filterValues;

        group.filters.forEach(filterDef => {
            if (!filterDef.selector) return;
            const activeValues = filterValues[filterDef.name] || [];
            $(filterDef.selector).each(function () {
                this.checked = activeValues.indexOf(this.value) !== -1;
            });
        });

        if (group.sort && group.sort.selector) {
            $(group.sort.selector).val(groupFilters.sortKey);
        }
    }

    // -------------------------------------------------------------------------
    // DOM -> URL (WHEN USER CHANGES FILTERS/SORT)
    // -------------------------------------------------------------------------

    /**
     * For a given group, read selected values from DOM inputs (checkboxes,
     * <select>), normalize/validate them, write them back to the URL, and then
     * re-apply filters for all groups.
     *
     * This is the "built-in wiring" for checkbox + select UI:
     *  • Filters: stored as CSV, removed when empty
     *  • Sort: only stored when NON-default
     *  • Page: reset to default by removing page param
     */
    function updateUrlFromGroupDom(group) {
        const params = getSearchParams();

        // --- FILTERS: read from DOM, normalize, write to URL ---
        group.filters.forEach(filterDef => {
            if (!filterDef.selector) return; // no built-in wiring for this filter

            const rawDomValues = $(filterDef.selector + ':checked')
                .map(function () {
                    return this.value;
                })
                .get();

            const normalizedValues = normalizeFilterList(
                rawDomValues,
                filterDef.allowedValues,
            );

            const urlKey = group.id + '_' + filterDef.param;

            if (normalizedValues.length) {
                // Non-empty -> store canonical CSV
                params.set(urlKey, normalizedValues.join(','));
            } else {
                // Empty -> remove param entirely
                params.delete(urlKey);
            }
        });

        // --- SORT: read from DOM, normalize, write ONLY if non-default ---
        if (group.sort && group.sort.selector) {
            const sortParamKey = group.id + '_' + group.sort.param;

            const rawSortKeyFromDom =
                $(group.sort.selector).val() || group.sort.defaultKey;

            const sortInfo = normalizeSortKey(rawSortKeyFromDom, group.sort);

            if (sortInfo.key === group.sort.defaultKey) {
                // Default sort -> keep URL clean
                params.delete(sortParamKey);
            } else {
                // Non-default sort -> write canonical key
                params.set(sortParamKey, sortInfo.key);
            }
        }

        // --- PAGINATION: reset to default page by REMOVING page param ---
        if (group.pagination) {
            const pageParamKey = group.id + '_' + (group.pagination.pageParam || 'page');
            // Default page is represented by the ABSENCE of this param.
            params.delete(pageParamKey);
        }

        // --- Commit URL (push/replace) and re-apply filters from URL ---
        commitUrl(params);
        applyFiltersFromUrl();
    }

    // -------------------------------------------------------------------------
    // FILTERING & SORTING
    // -------------------------------------------------------------------------

    /**
     * Check if an item matches all active filters for the group.
     *  • Within a single filter: OR logic (any selected value may match)
     *  • Across different filters: AND logic (must pass all filters)
     */
    function itemMatchesGroupFilters(item, group, groupFilters) {
        const filterValues = groupFilters.filterValues;
    
        for (let i = 0; i < group.filters.length; i++) {
            const filterDef = group.filters[i];
            const selectedValues = filterValues[filterDef.name] || [];
            if (!selectedValues.length) continue;
    
            const itemValues = filterDef.getItemValues(item) || [];
            const matchMode = filterDef.matchMode || 'any';
    
            let matchesThisFilter;
    
            if (matchMode === 'all') {
                matchesThisFilter = selectedValues.every(sel => {
                    return itemValues.indexOf(sel) !== -1;
                });
            } else {
                matchesThisFilter = selectedValues.some(sel => {
                    return itemValues.indexOf(sel) !== -1;
                });
            }
    
            if (!matchesThisFilter) return false;
        }
    
        return true;
    }

    /**
     * Sort a list of items according to a group's sort settings.
     */
    function sortItemsForGroup(items, group, groupFilters) {
        const field = groupFilters.sortField;
        const dir = groupFilters.sortDir === 'desc' ? -1 : 1;

        return items.slice().sort((a, b) => {
            let va = a[field];
            let vb = b[field];

            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;

            if (typeof va === 'string' && typeof vb === 'string') {
                return va.localeCompare(vb) * dir;
            }

            return (va - vb) * dir;
        });
    }

    // -------------------------------------------------------------------------
    // CORE: APPLY FILTERS FOR ALL GROUPS BASED ON URL (WITH PAGINATION)
    // -------------------------------------------------------------------------

    /**
     * Main engine pass:
     *  • Reads URL
     *  • Normalizes filters + sort
     *  • Canonicalizes URL (case/order, no defaults)
     *  • Syncs UI (checkboxes / selects)
     *  • Filters + sorts data
     *  • Applies pagination (optional)
     *  • Calls group.render(items, group, pageMeta)
     */
    function applyFiltersFromUrl() {
        if (isLoading) return;

        const params = getSearchParams();
        let urlChanged = false;

        groups.forEach(group => {
            const groupFilters = getGroupFiltersFromParams(group, params);

            if (canonicalizeGroupUrl(params, group, groupFilters)) {
                urlChanged = true;
            }

            syncGroupDomFromFilters(group, groupFilters);

            const last = lastFiltersByGroupId[group.id];
            if (!filtersEqual(groupFilters, last)) {
                const items = dataByKey[group.dataKey] || [];
                // log('ITEMS:', items);

                // 1) Filter + sort full list
                const filtered = items.filter(item =>
                    itemMatchesGroupFilters(item, group, groupFilters),
                );
                log('FILTERED:', filtered);
                const sorted = sortItemsForGroup(filtered, group, groupFilters);
                log('SORTED:', sorted);

                // 2) Pagination (optional)
                const pagination = group.pagination;
                let pageItems = sorted;
                let pageMeta = {
                    page: 1,
                    totalPages: 1,
                    totalItems: sorted.length,
                    pageSize: sorted.length || 1,
                };

                if (pagination) {
                    const pageParamKey = group.id + '_' + (pagination.pageParam || 'page');
                    const rawPage = params.get(pageParamKey);
                    const defaultPage = pagination.defaultPage || 1;

                    let currentPage = parseInt(rawPage, 10);
                    if (!Number.isFinite(currentPage) || currentPage < 1) {
                        currentPage = defaultPage;
                    }

                    const pageSize = pagination.pageSize || 20;
                    const totalItems = sorted.length;
                    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

                    if (currentPage > totalPages) {
                        currentPage = totalPages;
                    }

                    const start = (currentPage - 1) * pageSize;
                    const end = start + pageSize;
                    pageItems = sorted.slice(start, end);

                    pageMeta = {
                        page: currentPage,
                        totalPages,
                        totalItems,
                        pageSize,
                    };

                    // Canonicalize page param:
                    //  • Default page => remove param
                    //  • Non-default => set param
                    const pageParamCurrent = params.get(pageParamKey);

                    if (currentPage === defaultPage) {
                        if (pageParamCurrent !== null) {
                            params.delete(pageParamKey);
                            urlChanged = true;
                        }
                    } else {
                        const canonicalPageStr = String(currentPage);
                        if (pageParamCurrent !== canonicalPageStr) {
                            params.set(pageParamKey, canonicalPageStr);
                            urlChanged = true;
                        }
                    }
                }

                // 3) Custom per-group hook (NEW)
                if (typeof group.onStateChange === 'function') {
                    group.onStateChange(
                        {
                            filters: groupFilters.filterValues,
                            sortKey: groupFilters.sortKey,
                            page: pageMeta.page,
                            totalPages: pageMeta.totalPages,
                            totalItems: pageMeta.totalItems,
                            pageSize: pageMeta.pageSize,
                        },
                        group
                    );
                }

                // 4) Render page items (render can accept (items, group, pageMeta))
                group.render(pageItems, group, pageMeta);
                lastFiltersByGroupId[group.id] = groupFilters;
            }
        });

        if (urlChanged) {
            commitUrl(params);
        }
    }

    // -------------------------------------------------------------------------
    // PUBLIC API
    // -------------------------------------------------------------------------

    /**
     * Initialize engine:
     *  • Wires up change handlers for filter + sort controls (built-in wiring)
     *  • Wires up popstate (back/forward -> re-apply from URL)
     *  • Does NOT render until finishLoading() is called.
     */
    function init() {
        groups.forEach(group => {
            // Combine all filter selectors into one handler
            const filterSelectors = group.filters
                .map(f => f.selector)
                .filter(Boolean)
                .join(',');

            if (filterSelectors) {
                $(document).on('change', filterSelectors, function () {
                    updateUrlFromGroupDom(group);
                });
            }

            if (group.sort && group.sort.selector) {
                $(document).on('change', group.sort.selector, function () {
                    updateUrlFromGroupDom(group);
                });
            }
        });

        // Back/forward: re-apply filters from URL
        $(window).on('popstate', function () {
            applyFiltersFromUrl();
        });
    }

    /**
     * Set the data array for a given dataKey (per group.dataKey).
     * Call this when your API data arrives.
     */
    function setData(dataKey, items) {
        dataByKey[dataKey] = items || [];
    }

    /**
     * Mark data as fully loaded and perform the first render:
     *  • Turns off loading guard
     *  • Calls applyFiltersFromUrl() once
     */
    function finishLoading() {
        isLoading = false;
        applyFiltersFromUrl();
    }

    /**
     * Manually re-apply filters (read URL -> sync UI -> render).
     */
    function refresh() {
        applyFiltersFromUrl();
    }

    /**
     * Programmatically update filters + sortKey for a group.
     *
     * @param {string} groupId
     * @param {Object} options
     *   options.filters: { [filterName]: string[] }
     *   options.sortKey: string (optional)
     *   options.keepPage: boolean (if true, won't reset pagination to default)
     *
     * Example:
     *   engine.setGroupFilters('hotel', {
     *       filters: {
     *           services: ['hotel', 'albergue'],
     *           roomTypes: ['private'],
     *       },
     *       sortKey: 'rating-desc',
     *   });
     */
    function setGroupFilters(groupId, options = {}) {
        const group = groups.find(g => g.id === groupId);
        if (!group) return;

        const params = getSearchParams();
        const providedFilters = options.filters || {};

        log('setGroupFilters: provided filters', providedFilters);

        // Filters
        group.filters.forEach(filterDef => {
            const urlKey = group.id + '_' + filterDef.param;

            const hasProvidedFilter = Object.prototype.hasOwnProperty.call(
                providedFilters,
                filterDef.name
            );

            const existingValues = parseCsvParam(params, urlKey);

            const rawValues = hasProvidedFilter
                ? providedFilters[filterDef.name]   // update/clear this filter
                : existingValues;                   // preserve existing filter

            const normalizedValues = normalizeFilterList(
                rawValues,
                filterDef.allowedValues,
            );

            if (normalizedValues.length) {
                params.set(urlKey, normalizedValues.join(','));
            } else {
                params.delete(urlKey);
            }
        });

        // Sort
        const sortParamKey = group.id + '_' + group.sort.param;
        const existingSort = params.get(sortParamKey);
        const rawSortKey = options.sortKey || existingSort || group.sort.defaultKey;
        const sortInfo = normalizeSortKey(rawSortKey, group.sort);

        if (sortInfo.key === group.sort.defaultKey) {
            // Default sort -> no param
            params.delete(sortParamKey);
        } else {
            params.set(sortParamKey, sortInfo.key);
        }

        // Reset page to default when filters change, unless keepPage is explicitly true
        if (group.pagination && options.keepPage !== true) {
            const pageParamKey = group.id + '_' + (group.pagination.pageParam || 'page');
            // Represent default page by *no* param
            params.delete(pageParamKey);
        }

        commitUrl(params);
        applyFiltersFromUrl();
        
        return true; // Just so console doesn't show "undefined" as the result.
    }

    /**
     * Programmatically set the page for a group (for pagination controls).
     *
     * @param {string} groupId
     * @param {number} page
     *
     * Example:
     *   engine.setGroupPage('hotel', 3);
     */
    function setGroupPage(groupId, page) {
        const group = groups.find(g => g.id === groupId);
        if (!group || !group.pagination) return;

        const params = getSearchParams();
        const pagination = group.pagination;
        const pageParamKey = group.id + '_' + (pagination.pageParam || 'page');

        const defaultPage = pagination.defaultPage || 1;

        let p = parseInt(page, 10);
        if (!Number.isFinite(p) || p < 1) {
            p = defaultPage;
        }

        if (p === defaultPage) {
            // Default page -> no param
            params.delete(pageParamKey);
        } else {
            params.set(pageParamKey, String(p));
        }

        commitUrl(params);
        applyFiltersFromUrl();
    }

    return {
        init,
        setData,
        finishLoading,
        refresh,
        setGroupFilters,
        setGroupPage,
        historyMode,
    };
}


// ============================================================================
// CONFIG REFERENCE (HOW TO USE + WHAT EACH OPTION DOES)
// ============================================================================
//
// Top-level:
//   createFilterEngine({
//       historyMode: 'replace' | 'push',
//       groups: [ ...group configs... ],
//   })
//
//   • historyMode:
//       - 'replace' (default):
//           URL changes via history.replaceState.
//           Back button skips filter changes (jumps to previous page).
//       - 'push':
//           URL changes via history.pushState.
//           Back button steps through each filter/sort/page state.
//
//   • groups: array of group configs
//       Each group represents ONE visible list/section on the page.
//       Example: one group for "hotels", another group for "other listings".
//
// ---------------------------------------------------------------------------
// GROUP CONFIG (one entry inside groups[]):
//
//   {
//       id: 'hotel',               // unique ID, used as URL prefix (hotel_*)
//       dataKey: 'hotels',         // key for engine.setData('hotels', items)
//       cardContainer: '#hotel-cards', // your convenience only (render uses it)
//       filters: [ ...filter configs... ],
//       sort: { ...sort config... },
//       pagination: { ...pagination config... }, // optional
//       render: (items, group, pageMeta) => { ...draw cards... },
//   }
//
//   • id (string, required):
//       - Used to build URL param names: id + '_' + param.
//         E.g. id='hotel', filter.param='services' -> 'hotel_services'
//
//   • dataKey (string, required):
//       - Name under which you provide raw data via engine.setData(dataKey, items).
//
//   • cardContainer (string, optional but handy):
//       - CSS selector for where cards go. Used by your render() function.
//
//   • filters (array of filter configs, see below):
//       - Each entry defines ONE logical filter dimension (type, services, amenities, etc.).
//
//   • sort (object, required):
//       - Defines how sorting works for this group.
//
//   • pagination (object, optional):
//       - If present, group is paginated; otherwise renders all items.
//
//   • render(items, group, pageMeta):
//       - Called after filtering/sorting/pagination.
//       - Arguments:
//           items   -> array of items for the current page
//           group   -> the group config object itself
//           pageMeta-> { page, totalPages, totalItems, pageSize }
//
// ---------------------------------------------------------------------------
// FILTER CONFIG (inside group.filters[]):
//
//   {
//       name: 'services',                     // internal key in filterValues
//       param: 'services',                    // URL suffix (hotel_services)
//       selector: '[data-hotel-filter-service]', // checkboxes (optional)
//       allowedValues: ['albergue', 'hotel'], // canonical list & order
//       getItemValues: item => item.services || [],
//   }
//
//   • name (string, required):
//       - Logical name used in groupFilters.filterValues[name].
//       - Also used when calling engine.setGroupFilters('groupId', { filters: { name: [...] } }).
//
//   • param (string, required):
//       - Suffix for URL param. Combined with group.id.
//         Example: id='hotel', param='services' -> 'hotel_services'.
//
//   • selector (string, optional):
//       - CSS selector for built-in wiring of checkboxes.
//       - If provided:
//           - Engine listens to 'change' on those elements.
//           - When they change, URL is updated & list re-renders.
//       - If omitted or matches nothing:
//           - You must drive filters using engine.setGroupFilters() yourself.
//
//   • allowedValues (string[], required):
//       - Valid values for this filter, in canonical order & case.
//       - Engine:
//           - Removes unknown values
//           - Normalizes case
//           - Normalizes order in URL
//
//   • getItemValues(item) => string[]:
//       - Returns values from the item to compare against this filter.
//       - Example: item.services, item.hotel_features, [item.type], etc.
//       - An item passes this filter if any selected value appears in getItemValues(item).
//
// ---------------------------------------------------------------------------
// SORT CONFIG (group.sort):
//
//   sort: {
//       param: 'sort',                 // URL suffix => 'hotel_sort'
//       selector: '#hotel-sort-select',// <select> for built-in wiring (optional)
//       allowedKeys: ['title-asc', 'rating-desc'], // allowed sort keys
//       defaultKey: 'title-asc',       // default sort if nothing in URL
//       keyToSpec: key => {
//           const [field, dir] = (key || 'title-asc').split('-');
//           return { field: field || 'title', dir: dir || 'asc' };
//       },
//   }
//
//   • param (string):
//       - URL suffix. Combined with group.id -> 'hotel_sort', 'list_sort', etc.
//
//   • selector (string, optional):
//       - CSS selector for a <select> or equivalent.
//       - If present, engine listens to 'change', updates URL, re-renders.
//
//   • allowedKeys (string[]):
//       - List of valid sort keys, e.g. ['title-asc','rating-desc'].
//
//   • defaultKey (string):
//       - Used when no sort param in URL or invalid value.
//
//   • keyToSpec(key) => { field, dir }:
//       - Maps a sort key (e.g. 'rating-desc') to:
//           field: which property on item to sort by
//           dir: 'asc' | 'desc'
//
//   NOTE: If effective sort == defaultKey, sort param is removed from the URL
//   for clean URLs.
//
// ---------------------------------------------------------------------------
// PAGINATION CONFIG (group.pagination, optional):
//
//   pagination: {
//       pageParam: 'page',  // URL suffix => 'hotel_page'
//       pageSize: 20,       // items per page
//       defaultPage: 1,     // usually 1
//   }
//
//   • pageParam (string, default 'page'):
//       - URL suffix for the page number param.
//
//   • pageSize (number, default 20):
//       - Number of items to render per page.
//
//   • defaultPage (number, default 1):
//       - Used when no page param in URL or invalid value.
//       - If effective page == defaultPage, the page param is removed
//         from the URL, so you never see ?hotel_page=1 clutter.
//
// ---------------------------------------------------------------------------
// ENGINE API METHODS
// ---------------------------------------------------------------------------
//
//   const engine = createFilterEngine({ historyMode, groups });
//
//   engine.init()
//       - Call once after DOM ready.
//       - Wires up built-in events and popstate.
//
//   engine.setData(dataKey, items)
//       - Provide raw data for each group’s dataKey.
//         e.g. engine.setData('hotels', hotelArray);
//
//   engine.finishLoading()
//       - Call after you’ve set data for all groups.
//       - Turns off "loading" guard and triggers initial render
//         based on current URL.
//
//   engine.refresh()
//       - Manually re-read URL, sync UI, re-render all groups.
//
//   engine.setGroupFilters(groupId, { filters, sortKey, keepPage })
//       - Programmatically set filters and/or sort for a group.
//       - filters: { [filterName]: string[] }
//
//         Example:
//           engine.setGroupFilters('hotel', {
//               filters: {
//                   services: ['hotel', 'albergue'],
//                   roomTypes: ['private'],
//               },
//               sortKey: 'rating-desc', // optional
//               keepPage: false,        // default: false
//           });
//
//       - keepPage:
//           • false (default): reset page to default (remove page param).
//           • true: keep current page value in URL.
//
//   engine.setGroupPage(groupId, page)
//       - Programmatically set current page for paginated group.
//       - Example: engine.setGroupPage('hotel', 2);
//
//       - If page === defaultPage: page param is removed from URL.
//         Otherwise, it’s set to that page number.
//
// ============================================================================
// USAGE EXAMPLES (COMMENTED OUT)
// ============================================================================
//
// 1) Two groups (hotels + other listings) on the same page
//    with template-based rendering and pagination.
//    See earlier conversation for full HTML sketch.
// ---------------------------------------------------------------------------
/*
<script src="https://code.jquery.com/jquery-3.7.1.min.js"></script>
<script type="module">
    import { createFilterEngine } from '/js/filterEngine.js';

    // Example render functions using hidden templates:

    const hotelRender = (hotels, group, meta = {}) => {
        const $container = $('#hotel-cards');

        $container.find('.skeleton').remove();
        $container.children(':not([data-card-template="hotel"])').not('.skeleton').remove();

        const $template = $container.find('[data-card-template="hotel"]').first();
        if (!$template.length) return;

        if (!hotels.length) {
            $container.append('<p>No hotels match your filters.</p>');
            return;
        }

        const $frag = $(document.createDocumentFragment());

        hotels.forEach(h => {
            const $card = $template.clone(true, true);
            $card.removeAttr('hidden').removeClass('template');

            $card.find('.js-hotel-title').text(h.title);
            $card.find('.js-hotel-link').attr('href', h.link || '#');
            $card.find('.js-hotel-type').text(h.type || '');

            const services = (h.services || []).join(', ');
            const feats    = (h.hotel_features || []).join(', ');
            const rts      = (h.hotel_room_types || []).join(', ');

            $card.find('.js-hotel-services').text(services || '');
            $card.find('.js-hotel-features').text(feats || '');
            $card.find('.js-hotel-roomtypes').text(rts || '');

            $frag.append($card);
        });

        $container.append($frag);

        // Pagination UI
        const $pag = $('#hotel-pagination');
        if ($pag.length && meta.totalPages && meta.totalPages > 1) {
            $pag.show();
            $pag.find('.js-page-info').text(`Page ${meta.page} of ${meta.totalPages}`);
            $pag.find('[data-hotel-page="prev"]').prop('disabled', meta.page <= 1);
            $pag.find('[data-hotel-page="next"]').prop('disabled', meta.page >= meta.totalPages);
        } else if ($pag.length) {
            $pag.hide();
        }
    };

    const listingRender = (listings, group, meta = {}) => {
        const $container = $('#listing-cards');

        $container.find('.skeleton').remove();
        $container.children(':not([data-card-template="listing"])').not('.skeleton').remove();

        const $template = $container.find('[data-card-template="listing"]').first();
        if (!$template.length) return;

        if (!listings.length) {
            $container.append('<p>No listings match your filters.</p>');
            return;
        }

        const $frag = $(document.createDocumentFragment());

        listings.forEach(l => {
            const $card = $template.clone(true, true);
            $card.removeAttr('hidden').removeClass('template');

            $card.find('.js-listing-title').text(l.title);
            $card.find('.js-listing-link').attr('href', l.link || '#');
            $card.find('.js-listing-type').text(l.type || '');
            const services = (l.services || []).join(', ');
            $card.find('.js-listing-services').text(services || '');

            $frag.append($card);
        });

        $container.append($frag);
    };

    const groups = [
        {
            id: 'hotel',
            dataKey: 'hotels',
            cardContainer: '#hotel-cards',
            filters: [
                {
                    name: 'services',
                    param: 'services',
                    selector: '[data-hotel-filter-service]',
                    allowedValues: ['albergue', 'hotel'],
                    getItemValues: item => item.services || [],
                },
                {
                    name: 'features',
                    param: 'features',
                    selector: '[data-hotel-filter-feature]',
                    allowedValues: ['wifi', 'kitchen'],
                    getItemValues: item => item.hotel_features || [],
                },
                {
                    name: 'roomTypes',
                    param: 'roomTypes',
                    selector: '[data-hotel-filter-room-type]',
                    allowedValues: ['private', 'dorm'],
                    getItemValues: item => item.hotel_room_types || [],
                },
            ],
            sort: {
                param: 'sort',
                selector: '#hotel-sort-select',
                allowedKeys: ['title-asc', 'rating-desc', 'review_count-desc'],
                defaultKey: 'title-asc',
                keyToSpec: key => {
                    const [field, dir] = (key || 'title-asc').split('-');
                    return { field: field || 'title', dir: dir || 'asc' };
                },
            },
            pagination: {
                pageParam: 'page', // URL: hotel_page
                pageSize: 20,
                defaultPage: 1,
            },
            render: hotelRender,
        },
        {
            id: 'list',
            dataKey: 'listings',
            cardContainer: '#listing-cards',
            filters: [
                {
                    name: 'services',
                    param: 'services',
                    selector: '[data-list-filter-service]',
                    allowedValues: ['market', 'restaurant'],
                    getItemValues: item => item.services || [],
                },
            ],
            sort: {
                param: 'sort',
                selector: '#list-sort-select',
                allowedKeys: ['title-asc', 'rating-desc', 'review_count-desc'],
                defaultKey: 'title-asc',
                keyToSpec: key => {
                    const [field, dir] = (key || 'title-asc').split('-');
                    return { field: field || 'title', dir: dir || 'asc' };
                },
            },
            pagination: {
                pageParam: 'page', // URL: list_page
                pageSize: 20,
                defaultPage: 1,
            },
            render: listingRender,
        },
    ];

    const engine = createFilterEngine({
        historyMode: 'replace', // or 'push'
        groups,
    });

    engine.init();

    // Pagination controls for hotels
    $(document).on('click', '[data-hotel-page]', function () {
        const action = this.getAttribute('data-hotel-page');
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('hotel_page');
        const current = parseInt(raw, 10) || 1;

        if (action === 'prev') {
            engine.setGroupPage('hotel', current - 1);
        } else if (action === 'next') {
            engine.setGroupPage('hotel', current + 1);
        }
    });

    // Fetch data and feed into engine
    fetch('/api/listings')
        .then(res => res.json())
        .then(data => {
            const all = Object.values(data.listings).map(l => ({
                ...l,
                services: (l.services || []).filter(Boolean),
                hotel_room_types: (l.hotel_room_types || []).filter(Boolean),
                hotel_features:   (l.hotel_features || []).filter(Boolean),
            }));

            const hotels   = all.filter(l => l.type === 'hotel');
            const listings = all.filter(l => l.type !== 'hotel');

            engine.setData('hotels', hotels);
            engine.setData('listings', listings);

            engine.finishLoading();
        });
</script>
*/

